// Function index:
//   renderSecrets(host, ctx) — list stored secrets, "add new" form,
//       audit log. Refuses to show editor when SECRETS_KEK_BASE64 is
//       missing (a hard prerequisite — see deploy/README.md).

import {
  deleteAdminSecret, listAdminSecretAudit, listAdminSecrets, setAdminSecret,
  type SecretAuditEntry, type SecretMetadata,
} from '../../api.js';
import { esc, type AdminPanelCtx } from './helpers.js';

export async function renderSecrets(host: HTMLElement, ctx: AdminPanelCtx): Promise<void> {
  host.innerHTML = 'Loading…';
  try {
    const data = await listAdminSecrets();
    host.innerHTML = '';
    host.appendChild(secretsBlock(data.kek_configured, data.secrets, host, ctx));
  } catch (err) {
    host.innerHTML = '';
    ctx.setError(`secrets: ${(err as Error).message}`);
  }
}

function secretsBlock(
  kekConfigured: boolean,
  secrets: SecretMetadata[],
  host: HTMLElement,
  ctx: AdminPanelCtx,
): HTMLDivElement {
  const wrap = document.createElement('div');

  if (!kekConfigured) {
    wrap.innerHTML = `<div style="color:#ff8888;">SECRETS_KEK_BASE64 is not set in .env. Generate one with <code>npx tsx server/scripts/generate-kek.ts</code>, paste into .env, restart, and refresh.</div>`;
    return wrap;
  }

  const note = document.createElement('div');
  note.style.color = '#888';
  note.style.marginBottom = '8px';
  note.textContent = 'Plaintext values never leave the server. Setting a name UPSERTs and clears the in-memory cache; the next call reads the new value.';
  wrap.appendChild(note);

  // Existing rows.
  if (secrets.length === 0) {
    const empty = document.createElement('div');
    empty.style.color = '#888';
    empty.style.fontStyle = 'italic';
    empty.style.marginBottom = '12px';
    empty.textContent = '(no secrets stored yet — env fallback is in effect)';
    wrap.appendChild(empty);
  } else {
    for (const s of secrets) {
      wrap.appendChild(secretRow(s, host, ctx));
    }
  }

  // "Add new" form.
  const addForm = document.createElement('form');
  addForm.style.cssText = 'display:flex; gap:6px; margin-top:10px; align-items:center; flex-wrap:wrap;';
  addForm.innerHTML = `
    <input type="text" name="name" placeholder="NAME_UPPER_SNAKE" required pattern="[A-Z][A-Z0-9_]{0,62}" style="flex:0 0 220px;" />
    <input type="text" name="value" placeholder="value (never displayed after save)" required style="flex:1; min-width:200px;" />
    <input type="text" name="description" placeholder="description (optional)" style="flex:1; min-width:160px;" />
    <button type="submit">Save</button>
  `;
  addForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(addForm);
    const name = String(fd.get('name') ?? '').trim();
    const value = String(fd.get('value') ?? '');
    const description = String(fd.get('description') ?? '').trim();
    try {
      await setAdminSecret(name, value, description || undefined);
      addForm.reset();
      await renderSecrets(host, ctx);
    } catch (err) {
      ctx.setError(`secrets save: ${(err as Error).message}`);
    }
  });
  wrap.appendChild(addForm);

  // Audit log (newest first, capped at 100 entries on the server).
  const auditTitle = document.createElement('div');
  auditTitle.style.cssText = 'margin-top:16px; color:#888;';
  auditTitle.innerHTML = '<strong>Audit log</strong> <span style="font-size:11px;">(plaintext is never recorded)</span>';
  wrap.appendChild(auditTitle);

  const auditBody = document.createElement('div');
  auditBody.style.cssText = 'font-size:11px; color:#aaa; margin-top:6px;';
  auditBody.textContent = 'Loading…';
  wrap.appendChild(auditBody);

  // Fire and forget — populate when the request returns. Errors don't
  // block the rest of the Secrets panel from rendering.
  void listAdminSecretAudit().then(({ entries }) => {
    auditBody.innerHTML = '';
    if (entries.length === 0) {
      auditBody.style.fontStyle = 'italic';
      auditBody.textContent = '(no audit entries yet)';
      return;
    }
    for (const e of entries) {
      auditBody.appendChild(auditRow(e));
    }
  }).catch((err: Error) => {
    auditBody.innerHTML = '';
    auditBody.style.color = '#ff8888';
    auditBody.textContent = `audit: ${err.message}`;
  });

  return wrap;
}

function auditRow(e: SecretAuditEntry): HTMLDivElement {
  const row = document.createElement('div');
  row.style.cssText = 'padding:2px 0; display:flex; gap:8px;';
  const when = typeof e.at === 'string' ? e.at.slice(0, 19).replace('T', ' ') : '';
  const actor = e.actor_display_name ?? (e.actor_user_id ? `user ${e.actor_user_id}` : 'system');
  const verb = e.action === 'set' ? 'set' : 'deleted';
  const verbColor = e.action === 'delete' ? '#ff8888' : '#ffeb88';
  row.innerHTML = `<span style="color:#666; flex:0 0 130px;">${when}</span><span style="color:${verbColor}; flex:0 0 60px;">${verb}</span><span style="color:#ffeb88; flex:0 0 200px;">${esc(e.name)}</span><span style="color:#aaa;">by ${esc(actor)}</span>`;
  return row;
}

function secretRow(s: SecretMetadata, host: HTMLElement, ctx: AdminPanelCtx): HTMLDivElement {
  const row = document.createElement('div');
  row.style.cssText = 'padding:6px 0; border-bottom:1px solid #222; display:flex; align-items:center; gap:8px; flex-wrap:wrap;';
  const label = document.createElement('div');
  label.style.cssText = 'flex:0 0 220px; font-family:Consolas,monospace; color:#ffeb88;';
  label.textContent = s.name;
  const meta = document.createElement('div');
  meta.style.cssText = 'flex:1; color:#888; font-size:11px;';
  const updated = typeof s.updated_at === 'string' ? s.updated_at.slice(0, 19).replace('T', ' ') : '';
  meta.textContent = `v${s.version} · updated ${updated}${s.description ? ' · ' + s.description : ''}`;

  const rotate = document.createElement('button');
  rotate.type = 'button';
  rotate.textContent = 'Rotate';
  rotate.addEventListener('click', async () => {
    const value = prompt(`New value for ${s.name}:`);
    if (value === null || value === '') return;
    try {
      await setAdminSecret(s.name, value);
      await renderSecrets(host, ctx);
    } catch (err) {
      ctx.setError(`rotate: ${(err as Error).message}`);
    }
  });

  const del = document.createElement('button');
  del.type = 'button';
  del.textContent = 'Delete';
  del.style.cssText = 'background:#3a1818; color:#ff8888;';
  del.addEventListener('click', async () => {
    if (!confirm(`Delete secret ${s.name}? The server will fall back to process.env on the next read.`)) return;
    try {
      await deleteAdminSecret(s.name);
      await renderSecrets(host, ctx);
    } catch (err) {
      ctx.setError(`delete: ${(err as Error).message}`);
    }
  });

  row.appendChild(label);
  row.appendChild(meta);
  row.appendChild(rotate);
  row.appendChild(del);
  return row;
}
